import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Header from '../../main/components/Header';
import MainImageSlider from '../components/MainImageSlider';
import Description from '../components/Description';
import UserInfo from '../components/UserInfo';
import AuthorAds from '../components/AuthorAds';
import SimilarAds from '../components/SimilarAds';
import Path from '../../path/ui/Path';
import MobileBottomBar from '../../main/components/MobileBottomBar';

import getAnnouncementById from '../../../api/getAnnouncementById';
import '../style/main.css';
import Loading from '../../../common/components/Loading';
import eyeIcon from '../../../assets/icons/eye.svg';
import { trackEvent } from '../../../utils/AnalyticsUtils';
import DealerPage from '../components/DealerPage';

// TODO: Remove once backend returns real dealer ads
const DEMO_DEALER_ANNOUNCEMENT = {
    id: 'dealer-demo',
    is_dealer: true,
    title: 'Минеральды тыңайтқыш NPK 16-16-16',
    description: 'Жоғары сапалы минеральды тыңайтқыш NPK 16-16-16 — барлық ауылшаруашылық дақылдары үшін жарамды кешенді тыңайтқыш.\n\nҚасиеттері:\n• Азот, фосфор және калийдің тең мөлшері топырақ өнімділігін жан-жақты арттырады\n• Суда жақсы еритін гранулалар тез сіңеді\n• Кез келген топырақ түріне жарамды\n\nОрамасы: 50 кг қоржын. Буып-түю тығыз, ылғалдан қорғалған. Дилерден тікелей, делдалсыз жеткізіледі.',
    measurement_unit: 'тонна',
    image_urls: [],
    video_url: null,
    views: 124,
    created_at: new Date().toISOString(),
    rating: '4.8',
    reviews: 12,
    is_favorite: false,
    specifications: [
        { label: 'Азот (N)', value: '16%' },
        { label: 'Фосфор (P₂O₅)', value: '16%' },
        { label: 'Калий (K₂O)', value: '16%' },
        { label: 'Орамасы', value: '50 кг қоржын' },
        { label: 'Шығарылған елі', value: 'Ресей Федерациясы' },
        { label: 'Стандарт', value: 'ГОСТ 5956-78' },
        { label: 'Сақтау мерзімі', value: '6 ай' },
    ],
    // Бір тауарды бірнеше дистрибьютор/менеджер сата алады
    sellers: [
        {
            id: 1,
            name: 'АгроХим Дистрибьютор',
            avatar_url: null,
            rating: '4.9',
            reviews: 87,
            price: 185000,
            price_type: 'fixed',
            delivery_time: '5–7 күн',
            min_order: '5 т',
            location: { province: 'Алматы облысы', locality: 'Алматы' },
            in_stock: true,
        },
        {
            id: 2,
            name: 'КазАгроТрейд',
            avatar_url: null,
            rating: '4.7',
            reviews: 34,
            price: 182000,
            price_type: 'fixed',
            delivery_time: '3–5 күн',
            min_order: '10 т',
            location: { province: 'Астана', locality: 'Астана' },
            in_stock: true,
        },
        {
            id: 3,
            name: 'УралАгро Қазақстан',
            avatar_url: null,
            rating: '4.5',
            reviews: 19,
            price: 178000,
            price_type: 'fixed',
            delivery_time: '7–10 күн',
            min_order: '20 т',
            location: { province: 'Шымкент қ.', locality: 'Шымкент' },
            in_stock: true,
        },
        {
            id: 4,
            name: 'ФертиГрупп',
            avatar_url: null,
            rating: '4.6',
            reviews: 52,
            price: 190000,
            price_type: 'fixed',
            delivery_time: '2–4 күн',
            min_order: '3 т',
            location: { province: 'Алматы қ.', locality: 'Алматы' },
            in_stock: false,
        },
    ],
    // Legacy — regular announcement compatibility
    seller: { id: 0, name: 'Дилерлік', avatar_url: null, rating: '4.8', reviews: 12 },
    additional_announcements: [],
    similar_announcements: [],
};

const Main = () => {
    const { id } = useParams();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [bottomBarHidden, setBottomBarHidden] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Scroll to top when announcement ID changes
    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, [id]);

    useEffect(() => {
        if (id === 'dealer-demo') {
            setItem(DEMO_DEALER_ANNOUNCEMENT);
            setLoading(false);
            return;
        }

        const fetchItem = async () => {
            try {
                const response = await getAnnouncementById(id);
                setItem(response.announcement);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchItem();
    }, [id]);

    useEffect(() => {
        if (item) {
            trackEvent('announcement_view', {
                announcement_id: item.id,
                announcement_title: item.title,
                views: item.views || 0,
            });
        }
    }, [item]);

    if (loading) {
        return <Loading />;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    if (!item) {
        return <div>Item not found</div>;
    }

    const handleBottomBarToggle = (isHidden) => {
        setBottomBarHidden(isHidden);
    };

    return (
        <div className={`main_container ${isMobile ? 'mobile' : 'desktop'}`}>
            <Header onToggleMenu={handleBottomBarToggle} />
            <Path currentPath={item.title || 'Unknown Item'} />

            {item.is_dealer ? (
                /* ── Dealer: full rich layout ── */
                <div className="announcement_content">
                    <DealerPage item={item} isMobile={isMobile} />
                    {!bottomBarHidden && <MobileBottomBar activeTabName={'home'} />}
                </div>
            ) : (
                /* ── Regular announcement layout ── */
                <div className="announcement_content">
                    <div className="main_content">
                        <MainImageSlider
                            id={item.id}
                            images={Array.isArray(item.image_urls) && item.image_urls.length > 0 ? item.image_urls : []}
                            description={item.description || 'Нет описания'}
                            views={item.views || 0}
                            videoUrl={item.video_url || null}
                            isMobile={isMobile}
                        />
                        <div className="main_details">
                            <Description
                                id={item.id}
                                title={item.title}
                                date={item.created_at}
                                price={item.price}
                                price_type={item.price_type}
                                is_negotiable={item.is_negotiable}
                                barter={item.barter}
                                description={item.description}
                                phone={Array.isArray(item.contact_numbers) && item.contact_numbers.length > 0
                                    ? item.contact_numbers[0].phone_number
                                    : 'No phone available'}
                                rating={item.rating || ''}
                                reviews={item.reviews || ''}
                                user_id={item.seller.id}
                                is_favorite={item.is_favorite || false}
                            />
                            {isMobile &&
                                <div className="ad_description">
                                    <h3>Описание</h3>
                                    <p>{item.description || "Нет описания"}</p>
                                    <div className="ad_views">
                                        <div className='ad_views'>
                                            <img src={eyeIcon} alt='eye' />
                                            <span>Просмотров: {item.views || 0}</span>
                                        </div>
                                        <span>ID: {item.id}</span>
                                    </div>
                                </div>
                            }
                            <UserInfo
                                user={item.seller}
                                locations={item.location}
                            />
                        </div>
                    </div>
                    {Array.isArray(item.additional_announcements) && item.additional_announcements.length > 0 && (
                        <AuthorAds ads={item.additional_announcements} user={item.seller} />
                    )}
                    {Array.isArray(item.similar_announcements) && item.similar_announcements.length > 0 && (
                        <SimilarAds ads={item.similar_announcements} />
                    )}
                    {!bottomBarHidden && <MobileBottomBar activeTabName={'home'} />}
                </div>
            )}
        </div>
    );
};

export default Main;